import { StyleSheet, css } from 'aphrodite'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ReactMultiEmail, isEmail } from 'react-multi-email'
import { useUser } from '../../contexts/UserContext'
import { projectService } from '../../services/projectService'
import { userService } from '../../services/userService'
import type { Project, ProjectInvitation } from '../../types/project'
import type { Contact, User } from '../../types/user'
import { StakeholderType, UserType } from '../../types/user'

// Module-level cache for contact data to prevent refetching on remount
let cachedContactData: {
  brokers: Contact[]
  contractors: Contact[]
  subHaulers: Contact[]
  teamMembers: User[]
} | null = null

// Helper to format company address from structured fields
const formatCompanyAddress = (
  company:
    | {
        name?: string
        address?: string
        street_address?: string
        city?: string
        state?: string
        zip_code?: string
      }
    | undefined,
): string => {
  if (!company) return ''

  // If we have structured address fields, use them
  if (
    company.street_address ||
    company.city ||
    company.state ||
    company.zip_code
  ) {
    const parts = []
    if (company.street_address) parts.push(company.street_address)
    if (company.city) parts.push(company.city)
    if (company.state) parts.push(company.state)
    if (company.zip_code) parts.push(company.zip_code)
    return parts.join(', ')
  }

  // Fall back to full address
  return company.address || ''
}
import { getProjectContactFieldVisibility } from '../../utils/fieldVisibility'
import MultiSelect, { type Option } from '../MultiSelect'

interface ContactInfoStepProps {
  data: Partial<Project>
  onUpdate: (data: Partial<Project>) => void
  readOnly?: boolean
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
}

interface SiteSupervisorOption {
  id: number
  name: string
  email: string
  isTeamMember: boolean
  userId?: number
}

const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  data,
  onUpdate,
  readOnly = false,
  onValidationChange,
  showErrors = false,
}) => {
  const [brokers, setBrokers] = useState<Contact[]>([])
  const [contractors, setContractors] = useState<Contact[]>([])
  const [subHaulers, setSubHaulers] = useState<Contact[]>([])
  const [thirdPartyBrokers, setThirdPartyBrokers] = useState<Contact[]>([])
  const [thirdPartyContractors, setThirdPartyContractors] = useState<Contact[]>(
    [],
  )
  const [siteSupervisors, setSiteSupervisors] = useState<User[]>([])
  const [inviteEmails, setInviteEmails] = useState<string[]>([])
  const [isInviting, setIsInviting] = useState(false)
  const [existingInvitations, setExistingInvitations] = useState<
    ProjectInvitation[]
  >([])
  const [hasAutofilledBroker, setHasAutofilledBroker] = useState(false)
  const { user: currentUser } = useUser()
  // Ref to prevent refetching if component remounts
  const hasFetchedRef = useRef(false)

  // Get field visibility based on current user's stakeholder type
  const contactFieldVisibility = useMemo(() => {
    const stakeholderType = currentUser?.current_profile?.stakeholder_type
    return stakeholderType
      ? getProjectContactFieldVisibility(stakeholderType)
      : {
          showSubHauler: false,
          showThirdPartyBroker: false,
          showContractor: true,
          showThirdPartyContractor: true,
        }
  }, [currentUser?.current_profile?.stakeholder_type])

  // Validation: Check if at least one broker is selected (site supervisors optional)
  const brokerCount = Array.isArray(data.broker_contacts)
    ? data.broker_contacts.length
    : 0
  const hasBrokers = useMemo(() => brokerCount > 0, [brokerCount])

  useEffect(() => {
    console.log(
      'Project ContactInfoStep validation - broker count:',
      brokerCount,
      'isValid:',
      hasBrokers,
    )
    if (onValidationChange) {
      onValidationChange(hasBrokers)
    }
  }, [hasBrokers, onValidationChange, brokerCount])

  // Initialize with existing invitations
  useEffect(() => {
    if (data.invitations?.length) {
      setExistingInvitations(data.invitations)
    }
  }, [data.invitations])

  useEffect(() => {
    // Use cached data if available (prevents refetch on remount)
    if (cachedContactData) {
      const {
        brokers: cachedBrokers,
        contractors: cachedContractors,
        subHaulers: cachedSubHaulers,
        teamMembers,
      } = cachedContactData

      // Filter brokers to only show owners
      const brokerOwners = cachedBrokers.filter(
        (contact) => contact.contact_object.user_type === UserType.OWNER,
      )
      setBrokers(brokerOwners)
      setThirdPartyBrokers(brokerOwners)

      // Filter contractors to only show owners
      const contractorOwners = cachedContractors.filter(
        (contact) => contact.contact_object.user_type === UserType.OWNER,
      )
      setContractors(contractorOwners)
      setThirdPartyContractors(contractorOwners)

      // Filter sub haulers to only show owners
      setSubHaulers(
        cachedSubHaulers.filter(
          (contact) => contact.contact_object.user_type === UserType.OWNER,
        ),
      )

      // Filter team members to get only site supervisors
      setSiteSupervisors(
        teamMembers.filter(
          (user: User) => user.user_type === 'SITE_SUPERVISOR',
        ),
      )
      return
    }

    // Skip if already fetching (ref guard)
    if (hasFetchedRef.current) {
      return
    }
    hasFetchedRef.current = true

    const fetchContacts = async () => {
      try {
        const [brokerData, contractorData, subHaulerData, teamMembers] =
          await Promise.all([
            userService.getContactsByType(['BROKER']),
            userService.getContactsByType(['CONTRACTOR']),
            userService.getContactsByType(['SUB_HAULER']),
            userService.getTeamMembers(),
          ])

        // Cache the raw data
        cachedContactData = {
          brokers: brokerData,
          contractors: contractorData,
          subHaulers: subHaulerData,
          teamMembers,
        }

        // Filter brokers to only show owners
        const brokerOwners = brokerData.filter(
          (contact) => contact.contact_object.user_type === UserType.OWNER,
        )
        setBrokers(brokerOwners)
        setThirdPartyBrokers(brokerOwners)

        // Filter contractors to only show owners
        const contractorOwners = contractorData.filter(
          (contact) => contact.contact_object.user_type === UserType.OWNER,
        )
        setContractors(contractorOwners)
        setThirdPartyContractors(contractorOwners)

        // Filter sub haulers to only show owners
        setSubHaulers(
          subHaulerData.filter(
            (contact) => contact.contact_object.user_type === UserType.OWNER,
          ),
        )

        // Filter team members to get only site supervisors
        setSiteSupervisors(
          teamMembers.filter(
            (user: User) => user.user_type === 'SITE_SUPERVISOR',
          ),
        )
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      }
    }

    fetchContacts()
  }, [])

  // Auto-fill broker if current user is a broker (Item 79)
  useEffect(() => {
    if (
      hasAutofilledBroker ||
      readOnly ||
      !currentUser ||
      brokers.length === 0 ||
      (data.broker_contacts && data.broker_contacts.length > 0)
    ) {
      return
    }

    // Check if current user is a broker by stakeholder type
    const isBroker =
      currentUser.current_profile?.stakeholder_type ===
        StakeholderType.BROKER ||
      currentUser.stakeholder_types?.includes(
        StakeholderType.BROKER as unknown as string,
      )

    if (isBroker) {
      // Find the current user in the broker contacts list
      const currentUserBrokerContact = brokers.find(
        (contact) =>
          contact.contact_object.user_id === currentUser.id ||
          contact.contact_object.email === currentUser.email,
      )

      if (currentUserBrokerContact) {
        // Auto-select the current user as a broker
        const selectedBrokerUsers = [
          {
            id: currentUserBrokerContact.contact_object.id,
            first_name:
              currentUserBrokerContact.contact_object.name?.split(' ')[0] || '',
            last_name:
              currentUserBrokerContact.contact_object.name
                ?.split(' ')
                .slice(1)
                .join(' ') || '',
            email: currentUserBrokerContact.contact_object.email,
            user_type: UserType.OWNER,
            stakeholder_types: [StakeholderType.BROKER],
            username: currentUserBrokerContact.contact_object.email,
            phone_number: '',
            has_completed_driver_onboarding: false,
            has_completed_contractor_onboarding: false,
            current_profile: {
              id: currentUserBrokerContact.contact_object.id,
              stakeholder_type: StakeholderType.BROKER,
            },
          },
        ]

        onUpdate({
          broker_contacts: [currentUserBrokerContact.id],
          brokers: currentUserBrokerContact.contact_object.user_id
            ? [currentUserBrokerContact.contact_object.user_id]
            : [],
          broker_users: selectedBrokerUsers,
        })
        setHasAutofilledBroker(true)
      }
    }
  }, [
    brokers,
    currentUser,
    data.broker_contacts,
    hasAutofilledBroker,
    onUpdate,
    readOnly,
  ])

  const sendInvites = async (projectId: number) => {
    if (inviteEmails.length === 0) return

    setIsInviting(true)
    try {
      await Promise.all(
        inviteEmails.map((email) =>
          projectService.createProjectInvite(projectId, { email }),
        ),
      )
      setInviteEmails([]) // Clear emails after successful invite
    } catch (error) {
      console.error('Failed to send invites:', error)
    } finally {
      setIsInviting(false)
    }
  }

  // Only update pendingInvites when inviteEmails changes
  useEffect(() => {
    onUpdate({ pendingInvites: inviteEmails })
  }, [inviteEmails, onUpdate])

  const handleBrokerChange = (selected: Option | Option[] | null) => {
    const selectedOptions = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : []
    const selectedBrokers = selectedOptions
      .map((option) => Number.parseInt(option.value))
      .filter((id): id is number => !Number.isNaN(id))

    // Get the user IDs of the selected contacts
    const selectedUserIds = brokers
      .filter((b) => selectedBrokers.includes(b.id))
      .map((b) => b.contact_object.user_id)
      .filter((id): id is number => id !== undefined)

    // Create complete user objects for performance optimization
    const selectedBrokerUsers = brokers
      .filter((b) => selectedBrokers.includes(b.id))
      .map((b) => ({
        id: b.contact_object.id,
        first_name: b.contact_object.name?.split(' ')[0] || '',
        last_name: b.contact_object.name?.split(' ').slice(1).join(' ') || '',
        email: b.contact_object.email,
        user_type: UserType.OWNER,
        stakeholder_types: [StakeholderType.BROKER],
        username: b.contact_object.email,
        phone_number: '',
        has_completed_driver_onboarding: false,
        has_completed_contractor_onboarding: false,
        current_profile: {
          id: b.contact_object.id,
          stakeholder_type: StakeholderType.BROKER,
        },
      }))

    onUpdate({
      broker_contacts: selectedBrokers, // Keep for backward compatibility
      brokers: selectedUserIds, // User IDs for backend
      broker_users: selectedBrokerUsers, // Performance optimization
    })
  }

  const handleContractorChange = (selected: Option | Option[] | null) => {
    const selectedOptions = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : []
    const selectedContractors = selectedOptions
      .map((option) => Number.parseInt(option.value))
      .filter((id): id is number => !Number.isNaN(id))

    // Get the user IDs of the selected contacts
    const selectedUserIds = contractors
      .filter((c) => selectedContractors.includes(c.id))
      .map((c) => c.contact_object.user_id)
      .filter((id): id is number => id !== undefined)

    // Create complete user objects for performance optimization
    const selectedContractorUsers = contractors
      .filter((c) => selectedContractors.includes(c.id))
      .map((c) => ({
        id: c.contact_object.id,
        first_name: c.contact_object.name?.split(' ')[0] || '',
        last_name: c.contact_object.name?.split(' ').slice(1).join(' ') || '',
        email: c.contact_object.email,
        user_type: UserType.OWNER,
        stakeholder_types: [StakeholderType.CONTRACTOR],
        username: c.contact_object.email,
        phone_number: '',
        has_completed_driver_onboarding: false,
        has_completed_contractor_onboarding: false,
        current_profile: {
          id: c.contact_object.id,
          stakeholder_type: StakeholderType.CONTRACTOR,
        },
      }))

    onUpdate({
      contractor_contacts: selectedContractors, // Keep for backward compatibility
      contractors: selectedUserIds, // User IDs for backend
      contractor_users: selectedContractorUsers, // Performance optimization
    })
  }

  const handleSiteSupervisorChange = (selected: Option | Option[] | null) => {
    const selectedOptions = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : []
    const selectedSupervisorIds = selectedOptions
      .map((option) => Number.parseInt(option.value))
      .filter((id): id is number => !Number.isNaN(id))

    // Create complete user objects for performance optimization
    const selectedSupervisorUsers = siteSupervisors
      .filter((s) => selectedSupervisorIds.includes(s.id))
      .map((s) => ({
        id: s.id,
        first_name: s.first_name,
        last_name: s.last_name,
        email: s.email,
        user_type: s.user_type,
        stakeholder_types: s.stakeholder_types,
        username: s.username,
        phone_number: s.phone_number,
        has_completed_driver_onboarding: s.has_completed_driver_onboarding,
        has_completed_contractor_onboarding:
          s.has_completed_contractor_onboarding,
        current_profile: s.current_profile,
      }))

    onUpdate({
      site_supervisor_ids: selectedSupervisorIds,
      site_supervisor_users: selectedSupervisorUsers, // Performance optimization
    })
  }

  const handleSubHaulerChange = (selected: Option | Option[] | null) => {
    const selectedOptions = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : []
    const selectedSubHaulerIds = selectedOptions
      .map((option) => Number.parseInt(option.value))
      .filter((id): id is number => !Number.isNaN(id))

    // Get the user IDs of the selected contacts
    const selectedUserIds = subHaulers
      .filter((s) => selectedSubHaulerIds.includes(s.id))
      .map((s) => s.contact_object.user_id)
      .filter((id): id is number => id !== undefined)

    // Create complete user objects for performance optimization
    const selectedSubHaulerUsers = subHaulers
      .filter((s) => selectedSubHaulerIds.includes(s.id))
      .map((s) => ({
        id: s.contact_object.id,
        first_name: s.contact_object.name?.split(' ')[0] || '',
        last_name: s.contact_object.name?.split(' ').slice(1).join(' ') || '',
        email: s.contact_object.email,
        user_type: UserType.OWNER,
        stakeholder_types: [StakeholderType.SUB_HAULER],
        username: s.contact_object.email,
        phone_number: '',
        has_completed_driver_onboarding: false,
        has_completed_contractor_onboarding: false,
        current_profile: {
          id: s.contact_object.id,
          stakeholder_type: StakeholderType.SUB_HAULER,
        },
      }))

    onUpdate({
      sub_hauler_ids: selectedUserIds,
      sub_hauler_users: selectedSubHaulerUsers, // Performance optimization
    })
  }

  const handleThirdPartyBrokerChange = (selected: Option | Option[] | null) => {
    const selectedOptions = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : []
    const selectedThirdPartyBrokerIds = selectedOptions
      .map((option) => Number.parseInt(option.value))
      .filter((id): id is number => !Number.isNaN(id))

    // Get the user IDs of the selected contacts
    const selectedUserIds = thirdPartyBrokers
      .filter((b) => selectedThirdPartyBrokerIds.includes(b.id))
      .map((b) => b.contact_object.user_id)
      .filter((id): id is number => id !== undefined)

    onUpdate({
      third_party_broker_ids: selectedUserIds,
    })
  }

  const handleThirdPartyContractorChange = (
    selected: Option | Option[] | null,
  ) => {
    const selectedOptions = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : []
    const selectedThirdPartyContractorIds = selectedOptions
      .map((option) => Number.parseInt(option.value))
      .filter((id): id is number => !Number.isNaN(id))

    // Get the user IDs of the selected contacts
    const selectedUserIds = thirdPartyContractors
      .filter((c) => selectedThirdPartyContractorIds.includes(c.id))
      .map((c) => c.contact_object.user_id)
      .filter((id): id is number => id !== undefined)

    onUpdate({
      third_party_contractor_ids: selectedUserIds,
    })
  }

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>CONTACT INFORMATION</h3>
      <p className={css(styles.requiredNote)}>* Required field</p>

      <div className={css(styles.gridContainer)}>
        <div className={css(styles.formGroup)}>
          <label className={css(styles.label)}>Broker *</label>
          <div
            className={css(
              showErrors &&
                (!data.broker_contacts || data.broker_contacts.length === 0) &&
                styles.errorHighlight,
            )}
          >
            <MultiSelect
              isDisabled={readOnly}
              placeholder="Select Broker"
              options={useMemo(
                () =>
                  brokers.map((contact) => ({
                    label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                    value: contact.id.toString(),
                    name: contact.contact_object.name,
                    email: contact.contact_object.email,
                  })),
                [brokers],
              )}
              value={useMemo(
                () =>
                  data.broker_contacts
                    ?.map((contactId) => {
                      const contact = brokers.find((b) => b.id === contactId)
                      return contact
                        ? {
                            label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                            value: contact.id.toString(),
                            name: contact.contact_object.name,
                            email: contact.contact_object.email,
                          }
                        : null
                    })
                    .filter(Boolean) as Option[],
                [data.broker_contacts, brokers],
              )}
              onChange={handleBrokerChange}
            />
          </div>
          {showErrors &&
            (!data.broker_contacts || data.broker_contacts.length === 0) && (
              <div className={css(styles.errorMessage)}>
                Please select at least one broker to continue.
              </div>
            )}
        </div>

        <div className={css(styles.formGroup)}>
          <label className={css(styles.label)}>Contractor</label>
          <MultiSelect
            isDisabled={readOnly}
            placeholder="Select Contractor"
            options={useMemo(
              () =>
                contractors.map((contact) => ({
                  label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                  value: contact.id.toString(),
                  name: contact.contact_object.name,
                  email: contact.contact_object.email,
                })),
              [contractors],
            )}
            value={useMemo(
              () =>
                data.contractor_contacts
                  ?.map((contactId) => {
                    const contact = contractors.find((c) => c.id === contactId)
                    return contact
                      ? {
                          label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                          value: contact.id.toString(),
                          name: contact.contact_object.name,
                          email: contact.contact_object.email,
                        }
                      : null
                  })
                  .filter(Boolean) as Option[],
              [data.contractor_contacts, contractors],
            )}
            onChange={handleContractorChange}
          />
          {data.contractor_contacts &&
            data.contractor_contacts.length > 0 &&
            (() => {
              const contact = contractors.find(
                (c) => c.id === data.contractor_contacts?.[0],
              )
              return contact?.contact_object.company_name ? (
                <div className={css(styles.legalEntityInfo)}>
                  <div className={css(styles.legalEntityName)}>
                    {contact.contact_object.company_name}
                  </div>
                </div>
              ) : null
            })()}
        </div>

        <div className={css(styles.formGroup)}>
          <label className={css(styles.label)}>Site Supervisor</label>
          <MultiSelect
            isDisabled={readOnly}
            placeholder="Select Site Supervisor"
            options={useMemo(
              () =>
                siteSupervisors.map((user) => ({
                  label: `${user.first_name} ${user.last_name} (${user.email})`,
                  value: user.id.toString(),
                  name: `${user.first_name} ${user.last_name}`.trim(),
                  email: user.email,
                })),
              [siteSupervisors],
            )}
            value={useMemo(() => {
              // Use site_supervisor_ids for form data, fallback to site_supervisors for display
              let supervisorIds = data.site_supervisor_ids

              // If site_supervisor_ids is not set but we have site_supervisors (editing existing project)
              if (!supervisorIds && data.site_supervisors) {
                supervisorIds = data.site_supervisors.map((user) =>
                  typeof user === 'number' ? user : user.id,
                )
              }

              return (supervisorIds || [])
                .map((userId) => {
                  const user = siteSupervisors.find((u) => u.id === userId)
                  return user
                    ? {
                        label: `${user.first_name} ${user.last_name} (${user.email})`,
                        value: user.id.toString(),
                        name: `${user.first_name} ${user.last_name}`.trim(),
                        email: user.email,
                      }
                    : null
                })
                .filter(Boolean) as Option[]
            }, [
              data.site_supervisor_ids,
              data.site_supervisors,
              siteSupervisors,
            ])}
            onChange={handleSiteSupervisorChange}
          />
        </div>

        {/* Sub Hauler - visible when Broker creates the project */}
        {contactFieldVisibility.showSubHauler && (
          <div className={css(styles.formGroup)}>
            <label className={css(styles.label)}>Sub Hauler</label>
            <MultiSelect
              isDisabled={readOnly}
              placeholder="Select Sub Hauler"
              options={useMemo(
                () =>
                  subHaulers.map((contact) => ({
                    label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                    value: contact.id.toString(),
                    name: contact.contact_object.name,
                    email: contact.contact_object.email,
                  })),
                [subHaulers],
              )}
              value={useMemo(() => {
                // Use sub_hauler_ids for form data, fallback to sub_haulers for display
                let userIds = data.sub_hauler_ids

                // If sub_hauler_ids is not set but we have sub_haulers (editing existing project)
                if (!userIds && data.sub_haulers) {
                  userIds = data.sub_haulers.map((user) =>
                    typeof user === 'number' ? user : user.id,
                  )
                }

                return subHaulers
                  .filter((s) =>
                    (userIds || []).includes(s.contact_object.user_id || -1),
                  )
                  .map((contact) => ({
                    label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                    value: contact.id.toString(),
                    name: contact.contact_object.name,
                    email: contact.contact_object.email,
                  }))
              }, [data.sub_hauler_ids, data.sub_haulers, subHaulers])}
              onChange={handleSubHaulerChange}
            />
          </div>
        )}

        {/* Third-Party Broker - visible when Broker creates the project */}
        {contactFieldVisibility.showThirdPartyBroker && (
          <div className={css(styles.formGroup)}>
            <label className={css(styles.label)}>Third-Party Broker</label>
            <MultiSelect
              isDisabled={readOnly}
              placeholder="Select Third-Party Broker"
              options={useMemo(
                () =>
                  thirdPartyBrokers.map((contact) => ({
                    label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                    value: contact.id.toString(),
                    name: contact.contact_object.name,
                    email: contact.contact_object.email,
                  })),
                [thirdPartyBrokers],
              )}
              value={useMemo(() => {
                // Use third_party_broker_ids for form data, fallback to third_party_brokers for display
                let userIds = data.third_party_broker_ids

                // If third_party_broker_ids is not set but we have third_party_brokers (editing existing project)
                if (!userIds && data.third_party_brokers) {
                  userIds = data.third_party_brokers.map((user) =>
                    typeof user === 'number' ? user : user.id,
                  )
                }

                return thirdPartyBrokers
                  .filter((b) =>
                    (userIds || []).includes(b.contact_object.user_id || -1),
                  )
                  .map((contact) => ({
                    label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                    value: contact.id.toString(),
                    name: contact.contact_object.name,
                    email: contact.contact_object.email,
                  }))
              }, [
                data.third_party_broker_ids,
                data.third_party_brokers,
                thirdPartyBrokers,
              ])}
              onChange={handleThirdPartyBrokerChange}
            />
          </div>
        )}

        {/* Third-Party Contractor - visible when Contractor creates the project */}
        {contactFieldVisibility.showThirdPartyContractor && (
          <div className={css(styles.formGroup)}>
            <label className={css(styles.label)}>Third-Party Contractor</label>
            <MultiSelect
              isDisabled={readOnly}
              placeholder="Select Third-Party Contractor"
              options={useMemo(
                () =>
                  thirdPartyContractors.map((contact) => ({
                    label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                    value: contact.id.toString(),
                    name: contact.contact_object.name,
                    email: contact.contact_object.email,
                  })),
                [thirdPartyContractors],
              )}
              value={useMemo(() => {
                // Use third_party_contractor_ids for form data, fallback to third_party_contractors for display
                let userIds = data.third_party_contractor_ids

                // If third_party_contractor_ids is not set but we have third_party_contractors (editing existing project)
                if (!userIds && data.third_party_contractors) {
                  userIds = data.third_party_contractors.map((user) =>
                    typeof user === 'number' ? user : user.id,
                  )
                }

                return thirdPartyContractors
                  .filter((c) =>
                    (userIds || []).includes(c.contact_object.user_id || -1),
                  )
                  .map((contact) => ({
                    label: `${contact.contact_object.name} (${contact.contact_object.email})`,
                    value: contact.id.toString(),
                    name: contact.contact_object.name,
                    email: contact.contact_object.email,
                  }))
              }, [
                data.third_party_contractor_ids,
                data.third_party_contractors,
                thirdPartyContractors,
              ])}
              onChange={handleThirdPartyContractorChange}
            />
          </div>
        )}
      </div>

      {!readOnly && (
        <div className={css(styles.inviteSection)}>
          <h3 className={css(styles.sectionTitle)}>Invite Others</h3>

          {/* Show existing invitations */}
          {existingInvitations.length > 0 && (
            <div className={css(styles.existingInvites)}>
              <h4 className={css(styles.subSectionTitle)}>
                Current Invitations
              </h4>
              <div className={css(styles.inviteList)}>
                {existingInvitations.map((invitation, index) => (
                  <div key={invitation.id} className={css(styles.inviteItem)}>
                    <span>
                      {invitation.email}
                      <span
                        className={css(
                          invitation.accepted
                            ? styles.acceptedTag
                            : styles.pendingTag,
                        )}
                      >
                        {invitation.accepted ? 'Accepted' : 'Pending'}
                      </span>
                    </span>
                    <button
                      type="button"
                      className={css(styles.removeButton)}
                      onClick={async () => {
                        try {
                          // Only call API if we have project ID and it's a valid invitation
                          if (data.id && invitation.id) {
                            await projectService.deleteProjectInvite(
                              Number.parseInt(data.id.toString()),
                              invitation.id,
                            )
                          }

                          // Update local state
                          const updatedInvitations = [...existingInvitations]
                          updatedInvitations.splice(index, 1)
                          setExistingInvitations(updatedInvitations)
                          onUpdate({ invitations: updatedInvitations })
                        } catch (error) {
                          console.error('Failed to remove invitation:', error)
                        }
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h4 className={css(styles.subSectionTitle)}>New Invitations</h4>
          <div className={css(styles.inviteForm)}>
            <ReactMultiEmail
              enable={() => !readOnly}
              placeholder="Enter email addresses"
              emails={inviteEmails}
              onChange={setInviteEmails}
              validateEmail={isEmail}
              getLabel={(email, index, removeEmail) => (
                <div data-tag key={index}>
                  {email}
                  <button
                    type="button"
                    data-tag-handle
                    onClick={() => removeEmail(index)}
                  >
                    ×
                  </button>
                </div>
              )}
            />
          </div>
          <span className={css(styles.tooltip)}>
            Press enter to add email to invite list
          </span>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '20px',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px 24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  requiredNote: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '20px',
    fontStyle: 'italic',
  },
  subSectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '12px',
    color: '#4B5563',
  },
  formGroup: {
    marginBottom: '0',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
  },
  addSubhaulerBtn: {
    color: '#1C64F2',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '8px 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  inviteSection: {
    marginTop: '24px',
  },
  existingInvites: {
    marginBottom: '20px',
  },
  inviteList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  inviteItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    justifyContent: 'space-between',
  },
  pendingTag: {
    fontSize: '12px',
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    padding: '2px 8px',
    borderRadius: '4px',
    marginLeft: '8px',
  },
  acceptedTag: {
    fontSize: '12px',
    backgroundColor: '#D1FAE5',
    color: '#065F46',
    padding: '2px 8px',
    borderRadius: '4px',
    marginLeft: '8px',
  },
  removeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#6B7280',
    ':hover': {
      color: '#EF4444',
    },
  },
  inviteForm: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    ':global(.react-multi-email)': {
      flex: 1,
      backgroundColor: '#F9FAFB',
      border: '1px solid #E5E7EB',
      borderRadius: '6px',
      padding: '8px',
    },
  },
  tooltip: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '4px',
    display: 'block',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
  },
  errorHighlight: {
    borderRadius: '6px',
    boxShadow: '0 0 0 2px #FCD34D',
    backgroundColor: '#FFFBEB',
  },
  legalEntityInfo: {
    marginTop: '8px',
    padding: '8px 12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  legalEntityName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
  },
})

export default ContactInfoStep
